<template>
  <div>
    <!-- Where -->
    <b-card no-body class="border mb-2">
      <b-card-header class="pb-1">
        <div>
          <b-card-title class="mb-25">Which part of the church?</b-card-title>
          <b-card-text class="text-muted small mb-0">
            Choose the highest level that fits. Everything underneath is included
            automatically, so there is no need to list districts or assemblies one by one.
          </b-card-text>
        </div>
      </b-card-header>

      <b-card-body>
        <b-form-radio-group
          :checked="setup.scope_mode"
          :disabled="disabled"
          stacked
          class="mb-1"
          @change="update({ scope_mode: $event })"
        >
          <b-form-radio value="places" class="mb-50">
            <span class="font-weight-bold">Only certain places</span>
          </b-form-radio>
          <b-form-radio value="everywhere">
            <span class="font-weight-bold">Everywhere</span>
            <span class="text-muted small ml-50">the whole church, with no limit</span>
          </b-form-radio>
        </b-form-radio-group>

        <place-picker
          v-if="setup.scope_mode === 'places'"
          :value="setup.places"
          :disabled="disabled"
          @input="update({ places: $event })"
        />

        <b-alert v-else variant="warning" show class="mb-0">
          <div class="alert-body small">
            This client will reach every member record in PAC. Prefer a specific place
            unless the partner genuinely operates church-wide.
          </div>
        </b-alert>
      </b-card-body>
    </b-card>

    <!-- What, for members -->
    <b-card no-body class="border mb-2">
      <b-card-header class="pb-1">
        <div>
          <b-card-title class="mb-25">What may they do with member records?</b-card-title>
          <b-card-text class="text-muted small mb-0">
            Reference lists — member types, titles, departments and so on — are always
            readable, so the partner can turn ids into names.
          </b-card-text>
        </div>
      </b-card-header>

      <b-card-body>
        <b-form-radio-group
          :checked="setup.access"
          :disabled="disabled"
          stacked
          @change="update({ access: $event })"
        >
          <b-form-radio
            v-for="(level, index) in accessLevels"
            :key="level.value"
            :value="level.value"
            :class="index < accessLevels.length - 1 ? 'mb-1' : ''"
          >
            <span class="font-weight-bold" :class="level.danger ? 'text-danger' : ''">
              {{ level.label }}
            </span>
            <span v-if="level.recommended" class="badge badge-light-success ml-50">
              Recommended
            </span>
            <div class="text-muted small">{{ level.hint }}</div>
          </b-form-radio>
        </b-form-radio-group>
      </b-card-body>
    </b-card>

    <!-- What, for the map itself -->
    <b-card no-body class="border mb-2">
      <b-card-header class="pb-1">
        <div>
          <b-card-title class="mb-25">What may they do with places?</b-card-title>
          <b-card-text class="text-muted small mb-0">
            Regions, areas, districts, assemblies, mission blocks, RMCs and countries —
            the church's map, as opposed to the people on it. Most partners only need to
            read it.
          </b-card-text>
        </div>
      </b-card-header>

      <b-card-body>
        <b-form-radio-group
          :checked="setup.locations"
          :disabled="disabled"
          stacked
          @change="update({ locations: $event })"
        >
          <b-form-radio
            v-for="(level, index) in locationLevels"
            :key="level.value"
            :value="level.value"
            :class="index < locationLevels.length - 1 ? 'mb-1' : ''"
          >
            <span class="font-weight-bold" :class="level.danger ? 'text-danger' : ''">
              {{ level.label }}
            </span>
            <span v-if="level.recommended" class="badge badge-light-success ml-50">
              Recommended
            </span>
            <div class="text-muted small">{{ level.hint }}</div>
          </b-form-radio>
        </b-form-radio-group>

        <!-- The three rules that surprise people, stated where the choice is made -->
        <b-alert v-if="locationWrites" variant="secondary" show class="mt-1 mb-0">
          <div class="alert-body small">
            <div class="font-weight-bold mb-25">
              <feather-icon icon="InfoIcon" class="mr-25" />
              How the API guards these writes
            </div>
            <ul class="mb-0 pl-1">
              <li>
                Names and codes must be unique at their level, compared without regard to
                case. A clash is rejected, never merged.
              </li>
              <li v-if="restrictedToPlaces">
                Every new place must sit inside the places chosen above. A client limited
                to certain places cannot create a new <strong>region</strong> or
                <strong>mission block</strong> — those sit at the top of the tree, above
                any grant. Choose <em>Everywhere</em> if this partner needs to.
              </li>
              <li v-if="locationDeletes">
                Removing a place hides it rather than erasing it, and is only ever allowed
                for places <strong>this client created itself</strong> — the church's
                existing map cannot be deleted through the API by anyone.
              </li>
              <li v-if="locationDeletes">
                A place that members, the church map or stations-served still refer to
                cannot be removed until those are moved first.
              </li>
            </ul>
          </div>
        </b-alert>
      </b-card-body>
    </b-card>

    <!-- What this produces, so the generated payload is never a black box -->
    <b-alert variant="secondary" show class="mb-2">
      <div class="alert-body small">
        <feather-icon icon="InfoIcon" class="mr-50" />
        This grants access to <strong>{{ summary.resources }}</strong> resources under
        <strong>{{ summary.rules }}</strong> scope rules.
        <span v-if="summary.deletes" class="text-danger">
          Includes deleting member records.
        </span>
        <span v-else-if="summary.writes">Includes creating and updating members.</span>
        <span v-else>Members are read-only.</span>
        <span v-if="summary.location_deletes" class="text-danger">
          Places may be added, changed and removed.
        </span>
        <span v-else-if="summary.location_writes">Places may be added and changed.</span>
        <span v-else>Places are read-only.</span>
        Switch to <strong>Advanced</strong> to see or change every rule individually.
      </div>
    </b-alert>
  </div>
</template>

<script>
import {
  BCard,
  BAlert,
  BCardBody,
  BCardText,
  BCardTitle,
  BCardHeader,
  BFormRadio,
  BFormRadioGroup,
} from 'bootstrap-vue'

import PlacePicker from './PlacePicker.vue'
import { ACCESS_LEVELS, LOCATION_ACCESS_LEVELS, summarizeSetup } from './clientSetupPresets'

/**
 * The three questions that decide almost every real client: where, how much of the
 * people, and how much of the map. Everything else the API needs is derived from the
 * answers in `clientSetupPresets.js`.
 */
export default {
  components: {
    BCard,
    BAlert,
    BCardBody,
    BCardText,
    BCardTitle,
    BCardHeader,
    BFormRadio,
    BFormRadioGroup,
    PlacePicker,
  },
  props: {
    // { scope_mode, places, access, locations }
    value: {
      type: Object,
      required: true,
    },
    resources: {
      type: Array,
      default: () => [],
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      accessLevels: ACCESS_LEVELS,
      locationLevels: LOCATION_ACCESS_LEVELS,
    }
  },
  computed: {
    setup() {
      return this.value
    },
    summary() {
      return summarizeSetup(this.resources, this.value)
    },
    locationWrites() {
      return this.summary.location_writes
    },
    locationDeletes() {
      return this.summary.location_deletes
    },
    restrictedToPlaces() {
      return this.value.scope_mode === 'places'
    },
  },
  methods: {
    update(patch) {
      this.$emit('input', { ...this.value, ...patch })
    },
  },
}
</script>
